import Papa from 'papaparse';
import { PropApplicability } from 'BMapsSrc/model/ScopedPropertyGroup';
import { updateLocalJobState } from 'BMapsSrc/redux/jobs/access';
import { deepFreeze } from 'BMapsUtil/js_utils';
import { RXN_ENUM_REACTIONS } from './rxnEnumReactions';
import { JobDefinition, JobResultDestinations } from '../JobDefinition';

// Columns produced by rxn_enum's enumerated.csv:
//   reaction_name, direction, reactant_names, reactant_smiles,
//   product_smiles, inchi, inchikey
const scopedPropertyMetadata = deepFreeze({
    compound: {
        childGroups: {
            rxn_enum: {
                metadata: {
                    label: 'Reaction Enumeration',
                    tooltipHeading: 'Reaction enumeration products',
                },
                properties: {
                    product_smiles: {
                        metadata: {
                            label: 'Product',
                            structureValue: true,
                            defaultVisible: true,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    reaction_name: {
                        metadata: {
                            label: 'Reaction',
                            description: 'Named reaction (SMIRKS) that produced this product.',
                            defaultVisible: true,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    reactant_smiles: {
                        metadata: {
                            label: 'Reactants',
                            description: 'Building blocks combined for this product (SMILES).',
                            defaultVisible: true,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    reactant_names: {
                        metadata: {
                            label: 'Reactant Names',
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    inchikey: {
                        metadata: {
                            label: 'InChIKey',
                            defaultVisible: true,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    direction: {
                        metadata: {
                            label: 'Direction',
                            excludeFromTable: true,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    inchi: {
                        metadata: {
                            label: 'InChI',
                            excludeFromTable: true,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                },
            },
        },
    },
});

/**
 * rxn_enum generative job: applies named-reaction SMIRKS to building blocks and
 * parses the enumerated products from the results CSV onto local job state.
 *
 * @augments JobDefinition
 */
export class RxnEnumJob extends JobDefinition {
    static jobTypeId = 'rxn_enum';
    static resultDestination = JobResultDestinations.COMPOUND;
    static jobTypeName = 'Reaction Enumeration';
    static jobName = 'Reaction Enumeration Job';

    static scopedPropertyMetadata = scopedPropertyMetadata;

    /** Result rows are keyed by the product structure. */
    static idField = 'product_smiles';

    /**
     * Declarative description of this job's form — data only, no rendering
     * (see `formHelpers.js`). Like REINVENT, this job declares no `header`
     * and no submit framing: the embedding tab owns the program picker,
     * the job-name field, and the submit button.
     *
     * `molDataText` is the same `entryList` REINVENT uses — rxn_enum takes
     * the same "a list of ligand SMILES" input, so it gets the same widget
     * and the same `initData` seeding for free.
     *
     * The two reaction inputs are deliberately separate descriptors rather
     * than one: they're independent (named picks and drawn reactions run in
     * union, or either alone), and `rxn_enum_activity.py` already reads them
     * as two distinct request keys. Neither is `required` — blank
     * `reaction_names` means "enumerate over all 245 reactions", which is a
     * legitimate and common submission, not an unfilled form.
     * @type {object}
     */
    static jobForm = {
        body: {
            title: 'Specify Compound Input',
            fields: {
                molDataText: {
                    widget: 'entryList',
                    default: [],
                    required: true,
                    requiredMessage: 'Add some input compounds for reaction enumeration before submission',
                    addLabel: 'Add Ligand SMILES',
                    types: [
                        {
                            id: 'ligand_smiles',
                            unit: 'compound',
                            unitPlural: 'compounds',
                            placeholder: 'ligand smiles',
                            filetypes: ['smi'],
                        },
                    ],
                    initialValueFrom: (initData) => {
                        if (initData?.selectedCompounds?.length) {
                            return initData.selectedCompounds.map((x) => (
                                { type: 'ligand_smiles', data: x.getUnnamedSmiles() }
                            ));
                        }
                        if (initData?.molDataText) {
                            return initData.molDataText.split('\n').map((s) => s.trim()).filter(Boolean)
                                .map((data) => ({ type: 'ligand_smiles', data }));
                        }
                        return undefined;
                    },
                },
                reaction_names: {
                    widget: 'reactionSelect',
                    label: 'Reaction(s)',
                    default: [],
                    options: RXN_ENUM_REACTIONS,
                    placeholder: 'All reactions (type to search)',
                    helperText: 'Choose a named reaction(s), or leave blank to enumerate over all reactions.',
                },
                custom_rxnfiles: {
                    widget: 'reactionList',
                    default: [],
                    addLabel: '+ Draw a custom reaction',
                },
            },
        },
    };

    constructor(data = {}) {
        super(data);
        this.idField = this.constructor.idField;
    }

    /**
     * Turns the flat payload into the rxn_enum job-request payload.
     *
     * `reaction_names`/`custom_rxnfiles` never need an "is it empty" check
     * here: `deriveFormValues` drops empty arrays before this method is
     * called (see `formHelpers.js`'s `isEmpty`, which spells out the array
     * case precisely so array-valued widgets like these work), so an
     * untouched reaction picker simply doesn't appear in `payload` — which
     * is exactly what the backend reads as "use every reaction."
     *
     * `input_role`/`max_products_per_combo`/`max_unique` are fixed rather
     * than user-facing. The activity accepts all three, but the previous UI
     * always sent these same values, so they stay pinned here to keep this
     * a port rather than a behaviour change; promoting them to advanced
     * fields is a separate decision.
     * @param {object} payload
     * @returns {Promise<{ requestData?: object, error?: string[] }>}
     */
    static async buildRequestPayload(payload) {
        // Whole line, NOT just the first token — deliberately unlike
        // `ReinventJob`, which strips to the bare SMILES because REINVENT wants
        // that. rxn_enum does not: the activity writes these straight to
        // `run.smi`, and the tool's own `read_smiles_file()` reads everything
        // after the first whitespace token as the molecule's name
        // (`name = " ".join(parts[1:])`, falling back to `mol_<i>`). That name
        // is what populates the "Reactant Names" column in the results table,
        // so stripping it here would silently replace every user-supplied name
        // with `mol_1`, `mol_2`, … `isSmilesLine` accepts "<smiles> <name>"
        // lines, so they reach this method intact.
        const smiles = (payload.molDataText || [])
            .map((entry) => entry.data.trim())
            .filter((s) => s !== '');
        const { reaction_names: reactionNames, custom_rxnfiles: customRxnfiles } = payload;
        return {
            requestData: {
                smiles,
                reaction_names: reactionNames,
                custom_rxnfiles: customRxnfiles,
                input_role: 'reactant',
                max_products_per_combo: 50,
                max_unique: 0,
            },
        };
    }

    /**
     * Parses the rxn_enum results CSV into structure rows and stores them on
     * local job state (same shape as the REINVENT job).
     * @override
     */
    async integrateResults(job, results) {
        let structures = null;
        if (results.jobResults?.results) {
            structures = Papa.parse(
                results.jobResults.results,
                { header: true, dynamicTyping: true, skipEmptyLines: true }
            ).data
                // Drop rows with no structure (e.g. the trailing blank line) so the
                // workspace loader never builds a load-mol-data spec with no molData.
                .filter((row) => (row?.[this.idField] ?? '').toString().trim())
                // JobsResultsTab loads each compound from a hardcoded `row.SMILES`
                // (the REINVENT contract), ignoring the `structureValue` flag. Our
                // structure column is `product_smiles`, so alias it to `SMILES` or
                // molData comes out empty -> "No molData item in load-mol-data json spec".
                .map((row) => ({ ...row, SMILES: row[this.idField] }));
        }
        updateLocalJobState(job.jobid, { structures });
        return { results };
    }
}
